import { useNavigate } from 'react-router-dom';
import { CheckCircle, AlertTriangle } from 'lucide-react';

function Btn({ primary, children, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="font-body font-medium"
      style={{
        padding: '0.7rem 1.4rem', borderRadius: 6,
        border: primary ? 'none' : '1px solid var(--b2)',
        background: primary ? 'var(--a)' : 'transparent',
        color: primary ? '#fff' : 'var(--white)', cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export default function VerifySuccess({ domain, verifiedAt, subdomainsGranted = 0, status, onReverify }) {
  const navigate = useNavigate();

  // Expired state.
  if (status === 'expired') {
    return (
      <div style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
        <div style={{ width: 80, height: 80, borderRadius: 999, margin: '0 auto 1.5rem',
              background: 'rgba(220,38,38,0.12)', display: 'flex', alignItems: 'center',
              justifyContent: 'center' }}>
          <AlertTriangle size={40} style={{ color: 'var(--danger)' }} />
        </div>
        <h1 className="font-display" style={{ fontSize: '2rem', fontWeight: 700, color: 'var(--white)' }}>
          Verification expired
        </h1>
        <p className="font-body" style={{ color: 'var(--ts)', fontSize: '1.05rem', marginTop: '0.6rem' }}>
          We could no longer confirm ownership of {domain}, so monitoring is paused.
        </p>
        <div style={{ marginTop: '1.75rem' }}>
          <Btn primary onClick={onReverify} testid="verify-reverify">Re-verify your domain →</Btn>
        </div>
      </div>
    );
  }

  // "Already verified" minimal state (landed here later).
  const olderThan5Min = (() => {
    if (!verifiedAt) return false;
    const t = Date.parse(verifiedAt);
    return Number.isFinite(t) && (Date.now() - t) > 5 * 60 * 1000;
  })();

  if (olderThan5Min) {
    return (
      <div style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
        <div style={{ width: 64, height: 64, borderRadius: 999, margin: '0 auto 1.25rem',
              background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center',
              justifyContent: 'center' }}>
          <CheckCircle size={34} style={{ color: 'var(--ok)' }} />
        </div>
        <h1 className="font-display" style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--white)' }}>
          Already verified
        </h1>
        <p className="font-body" style={{ color: 'var(--ts)', fontSize: '1rem', marginTop: '0.5rem' }}>
          {domain} is verified and being monitored.
        </p>
        <div style={{ marginTop: '1.5rem' }}>
          <Btn primary onClick={() => navigate('/dashboard')} testid="verify-go-dashboard">
            Go to dashboard →
          </Btn>
        </div>
      </div>
    );
  }

  // Fresh success.
  return (
    <div style={{ textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
      <div style={{ width: 80, height: 80, borderRadius: 999, margin: '0 auto 1.5rem',
            background: 'rgba(34,197,94,0.12)', display: 'flex', alignItems: 'center',
            justifyContent: 'center' }}>
        <CheckCircle size={44} style={{ color: 'var(--ok)' }} />
      </div>
      <h1 className="font-display" style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--white)' }}>
        Domain verified
      </h1>
      <p className="font-body" style={{ color: 'var(--white)', fontSize: '1.25rem', marginTop: '0.5rem' }}>
        {domain} is now being monitored
      </p>
      <p className="font-body" style={{ color: 'var(--ts)', fontSize: '0.95rem', marginTop: '1rem',
            lineHeight: 1.6 }}>
        We&apos;re running your first security scan now. This usually takes under 30 seconds.<br />
        Your first report will appear on your dashboard.
      </p>

      <div style={{ display: 'flex', gap: '0.85rem', justifyContent: 'center', marginTop: '1.75rem',
            flexWrap: 'wrap' }}>
        <Btn primary onClick={() => navigate('/dashboard')} testid="verify-go-dashboard">
          Go to dashboard →
        </Btn>
        <Btn onClick={() => navigate('/dashboard/domains')} testid="verify-another">
          Verify another domain
        </Btn>
      </div>

      {subdomainsGranted > 0 && (
        <p className="font-body" style={{ color: 'var(--ts)', fontSize: '0.82rem', marginTop: '1.5rem' }}>
          We also auto-verified {subdomainsGranted} subdomain{subdomainsGranted === 1 ? '' : 's'} under this account.
        </p>
      )}
    </div>
  );
}
